import { ACTION_SORT_TAG } from '../api/action-sort';
import { apiUrl } from '../api/api-url';

/**
 * 036 (issue 208, FR-021, research R-008) — which `GET`s may be stored and served from storage at
 * all. Started as exactly home's five reads (issue 208's own slice); issue 221 ("the other ten
 * readable screens") widens it to every read FR-021's eleven readable screens make: home, the
 * contexts list, the projects list, a single context, a single project, the tickler, the tags list,
 * a single tag, review, the recurring list, and the first page of done. Nothing else — `/me`,
 * `/me/preferences`, search, stats and every maintenance surface stay off this list, which is what
 * keeps FR-025 true structurally rather than by a screen remembering not to ask.
 *
 * **A structural match, not a table of literal strings.** A single context, project or tag carries
 * an id or a name in its own URL, and the container-detail and tag screens' `view=all`/`view=completed`
 * reads carry a container id that is a practitioner's to have many of — so this matches the SHAPE of
 * a request (which query keys are present, and which values a closed vocabulary like `view` or `sort`
 * is allowed to hold), never one fixed value.
 *
 * **The expected PATHS are derived through `apiUrl()`, never written as bare `/api/v1/…`
 * literals.** `apiUrl()` prefixes `appConfig().apiBaseUrl`, which is an ABSOLUTE origin in
 * development (`http://localhost:5176`) but a RELATIVE path in production
 * (`environment.prod.ts`'s `/api-host`, proxied same-origin). A request's actual `pathname` is
 * therefore `/api-host/api/v1/contexts` in the built application and `/api/v1/contexts` in
 * development — a bare literal here matched only the latter and silently matched NOTHING in a
 * production build, which is exactly the build `e2e-pwa/` runs against. Measured: every
 * `e2e-pwa/` spec that depends on a stored view existing at all failed this way before this fix,
 * while every `core/stored-views/` unit test (whose `TEST_API` carries no extra path segment)
 * stayed green throughout — a guard that agrees with its own blind spot.
 *
 * Stated once, here: `core/stored-views/storing-client.ts` is this predicate's only caller.
 */

/** The three `view`s home and the tickler read whole, always with `view` + `pageSize` and nothing
 *  else — `home.store.ts`'s `homeViewUrls()` and `tickler.store.ts`'s `readDeferred()` build these,
 *  and the tickler's is byte-identical to home's own `deferred` read, so it needs no branch of its
 *  own here. */
const ACTIONS_SIMPLE_VIEWS: ReadonlySet<string> = new Set(['available', 'deferred', 'completed-recent']);

const currentBaseURI = (): string | undefined => (typeof document === 'undefined' ? undefined : document.baseURI);

/** The `pathname` a request to `path` actually carries, under WHATEVER `apiBaseUrl` is configured
 *  right now — recomputed on every call, never cached, so a config read before `setAppConfig` runs
 *  (or a later change to it) can never leave this holding a stale answer. */
function pathnameFor(path: string): string {
  return new URL(apiUrl(path), currentBaseURI()).pathname;
}

/** Does `params` carry exactly these keys — no more, no fewer? Order-independent, since
 *  `withQuery` builds from a plain object and nothing here should depend on property order. */
function hasExactly(params: URLSearchParams, keys: readonly string[]): boolean {
  const present = [...params.keys()];
  if (present.length !== keys.length) return false;
  return keys.every((key) => params.has(key));
}

/** `/api/v1/contexts/{id}` or `/api/v1/projects/{id}` — a single container, no query string
 *  (`container.store.ts`'s `readContainer`). `{id}` is never checked against anything but being
 *  present and free of a further `/` — the id itself is the practitioner's, not a value this
 *  predicate has an opinion about. */
function isSingleContainerPath(pathname: string, plural: 'contexts' | 'projects'): boolean {
  const base = `${pathnameFor(`/api/v1/${plural}`)}/`;
  if (!pathname.startsWith(base)) return false;
  const rest = pathname.slice(base.length);
  return rest.length > 0 && !rest.includes('/');
}

/**
 * `/api/v1/actions` alone: the three simple views, the container detail screens' `view=all`
 * (`sort=tag` + exactly one of `contextId`/`projectId`) and `view=completed` (the same id, no
 * `sort`) reads, and the tag screen's `view=all`/`view=completed` reads (`tag=` in place of a
 * container id). The archive's first page (`view=completed`, `page=1`, optionally narrowed by the
 * same container ids) is its own branch — it is the only one of these that ever carries `page`.
 */
function isAllowedActionsQuery(params: URLSearchParams): boolean {
  const view = params.get('view');
  if (view === null) return false;

  if (ACTIONS_SIMPLE_VIEWS.has(view)) return hasExactly(params, ['view', 'pageSize']);

  const hasContextId = params.has('contextId');
  const hasProjectId = params.has('projectId');
  const exactlyOneContainerId = hasContextId !== hasProjectId;
  const containerIdKey = hasContextId ? 'contextId' : 'projectId';

  if (view === 'all') {
    if (params.has('tag')) return hasExactly(params, ['view', 'tag', 'pageSize']);
    if (!exactlyOneContainerId) return false;
    return params.get('sort') === ACTION_SORT_TAG
      && hasExactly(params, ['view', 'sort', 'pageSize', containerIdKey]);
  }

  if (view === 'completed') {
    if (params.has('page')) {
      // The archive's first page ONLY (FR-021: "done is readable for its first page only") —
      // `archive.store.ts`'s `readCompletedPage`, unnarrowed or narrowed by at most one container id.
      if (params.get('page') !== '1') return false;
      if (hasContextId && hasProjectId) return false;
      const keys = ['view', 'page', 'pageSize', ...(hasContextId ? ['contextId'] : []), ...(hasProjectId ? ['projectId'] : [])];
      return hasExactly(params, keys);
    }
    if (params.has('tag')) return hasExactly(params, ['view', 'tag', 'pageSize']);
    if (!exactlyOneContainerId) return false;
    // The container detail screens' completed-recent read (`container.store.ts`'s
    // `readActionsCompleted`) carries no `sort` at all — deliberately (FR-009's own note).
    return hasExactly(params, ['view', 'pageSize', containerIdKey]);
  }

  return false;
}

/**
 * True for a `GET` whose URL is one of the eleven readable screens' own reads. Anything else —
 * including a request to `/api/v1/actions` with a shape none of them make — is neither stored nor
 * served from storage.
 */
export function isAllowedStoredViewUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, currentBaseURI());
  } catch {
    return false;
  }

  const { pathname, searchParams: params } = parsed;

  if (pathname === pathnameFor('/api/v1/contexts')) {
    // The whole list, unpaged by any filter: `pageSize` alone. Read by home, the contexts list, the
    // container/tag/tickler screens' own lookups, and — via `page(contextsUrl())` — the recurring
    // screen's own create-form vocabulary.
    return hasExactly(params, ['pageSize']);
  }
  if (pathname === pathnameFor('/api/v1/projects')) {
    // `pageSize` + `state=all` (home, the projects list, review, the container/tag lookups) OR
    // `pageSize` alone (`recurring.store.ts`'s own `page(projectsUrl())`, which never sends `state`).
    return hasExactly(params, ['pageSize', 'state']) || hasExactly(params, ['pageSize']);
  }
  if (pathname === pathnameFor('/api/v1/tags')) {
    // The whole vocabulary, no query string at all (`tags.store.ts`'s `readTags`).
    return hasExactly(params, []);
  }
  if (pathname === pathnameFor('/api/v1/recurring-actions')) {
    // Unpaginated by contract (research R-011); `recurring.store.ts`'s `read()`.
    return hasExactly(params, []);
  }
  if (pathname === pathnameFor('/api/v1/actions')) {
    return isAllowedActionsQuery(params);
  }
  if (isSingleContainerPath(pathname, 'contexts') || isSingleContainerPath(pathname, 'projects')) {
    return hasExactly(params, []);
  }
  return false;
}
